import { useState } from 'react'

import { Button, Modal, Trans } from '@/components'

import type { MemberTarget } from './AssignCreditDialog'

type Props = {
  open: boolean
  target: MemberTarget
  reclaimAmount: number
  onClose: () => void
  onConfirm: () => Promise<void> | void
}

function formatCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function ConfirmReclaimDialog({ open, target, reclaimAmount, onClose, onConfirm }: Props) {
  const [loading, setLoading] = useState(false)

  const afterBalance = (target?.currentCredit ?? 0) / 100 - reclaimAmount

  async function handleConfirm() {
    setLoading(true)
    try {
      await onConfirm()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[480px]">
      <Modal.Header>
        <Trans i18nKey="team.member.dialog.confirmCreditReclaim">Confirm Credit Reclaim</Trans>
      </Modal.Header>

      <Modal.Body>
        <p className="text-secondary t-sm">
          <Trans
            i18nKey="team.member.dialog.confirmReclaimCreditsFrom"
            values={{
              credits: formatCredits(reclaimAmount),
              name: target?.name,
              balance: formatCredits(afterBalance),
            }}
            components={{
              1: <span className="font-semibold text-primary" />,
              3: <span className="text-highlight" />,
              5: <span className="font-semibold text-primary" />,
            }}
          >
            Are you sure you want to reclaim{' '}
            <span className="font-semibold text-primary">
              {formatCredits(reclaimAmount)} Credits
            </span>{' '}
            from <span className="text-highlight">{target?.name}</span>? This will decrease their
            balance to{' '}
            <span className="font-semibold text-primary">
              {formatCredits(afterBalance)} Credits
            </span>
            .
          </Trans>
        </p>

        <p className="mt-4 text-secondary t-sm">
          <Trans i18nKey="team.member.dialog.reclaimWarning">
            This action may cause the member to have insufficient funds, potentially impacting
            active resources.
          </Trans>
        </p>
      </Modal.Body>

      <Modal.Footer>
        <Button type="outline" className="min-w-[88px]" onClick={onClose} disabled={loading}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button type="danger" className="min-w-[145px]" onClick={handleConfirm} disabled={loading}>
          <Trans i18nKey="team.member.dialog.confirmReclaim">Confirm Reclaim</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
